import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Download, LogOut, Users, CalendarCheck, Phone, MailCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { AdminFilters, applyFilters } from "@/components/admin/AdminFilters";

const inputClass =
  "w-full bg-transparent border-b border-black/20 focus:border-[#0D0D0D] outline-none py-4 text-[#0D0D0D] placeholder:text-[#0D0D0D]/35 transition-colors duration-500";

export default function Admin() {
  const [auth, setAuth] = useState(null); // null = checking
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({ q: "", from: "", to: "" });

  const loadData = async () => {
    try {
      const { data } = await api.get("/admin/prereservations");
      setData(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => {
    api.get("/auth/me")
      .then(({ data }) => { setAuth(data); loadData(); })
      .catch(() => setAuth(false));
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLoginError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setAuth(data);
      loadData();
    } catch (err) {
      setLoginError(formatApiError(err, "Connexion impossible"));
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    await api.post("/auth/logout");
    setAuth(false);
    setData(null);
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/prereservations/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "solamove-prereservations.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export CSV téléchargé");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (auth === null) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="animate-spin text-[#0D0D0D]" size={28} />
      </div>
    );
  }

  if (!auth) {
    return (
      <div data-testid="admin-login" className="min-h-screen bg-white flex items-center justify-center px-6">
        <div className="w-full max-w-md">
          <Link to="/" className="font-display font-bold tracking-[0.35em] text-sm">SOLAMOVE</Link>
          <h1 className="font-display uppercase tracking-tight text-3xl mt-10 mb-2">Espace admin</h1>
          <p className="text-[#6E6A66] text-sm mb-10">Consultez et exportez les pré-réservations.</p>
          <form onSubmit={login} className="space-y-6">
            <input data-testid="admin-input-email" type="email" placeholder="Email" value={email}
              onChange={(e) => setEmail(e.target.value)} className={inputClass} autoComplete="email" required />
            <input data-testid="admin-input-password" type="password" placeholder="Mot de passe" value={password}
              onChange={(e) => setPassword(e.target.value)} className={inputClass} autoComplete="current-password" required />
            {loginError && <p data-testid="admin-login-error" className="text-[#B4552D] text-sm">{loginError}</p>}
            <button data-testid="admin-login-button" type="submit" disabled={loading}
              className="w-full bg-[#0D0D0D] text-[#FAF8F5] font-display uppercase tracking-[0.15em] text-sm py-5 rounded-full disabled:opacity-60 flex items-center justify-center gap-3">
              {loading && <Loader2 size={16} className="animate-spin" />}
              Se connecter
            </button>
          </form>
        </div>
      </div>
    );
  }

  const items = data?.items || [];
  const filtered = applyFilters(items, filters);
  const today = new Date().toISOString().slice(0, 10);
  const todayCount = items.filter((i) => (i.created_at || "").startsWith(today)).length;
  const withPhone = items.filter((i) => i.phone).length;
  const marketing = items.filter((i) => i.marketing_consent).length;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-white">
      <header className="border-b border-black/[0.08]">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 h-20 flex items-center justify-between">
          <Link to="/" className="font-display font-bold tracking-[0.35em] text-sm">SOLAMOVE</Link>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block font-mono-alt text-[10px] uppercase tracking-[0.2em] text-[#736F6E]">{auth.email}</span>
            <button data-testid="admin-logout-button" onClick={logout}
              className="flex items-center gap-2 text-[11px] uppercase tracking-[0.15em] text-[#6E6A66] hover:text-[#0D0D0D] transition-colors">
              <LogOut size={14} /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 sm:px-10 py-14">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12">
          <div>
            <p className="font-mono-alt text-xs uppercase tracking-[0.3em] text-[#736F6E]">Pré-lancement</p>
            <h1 className="font-display uppercase tracking-tight text-4xl sm:text-5xl mt-3">Pré-réservations</h1>
            <p data-testid="admin-env-note" className="mt-3 text-xs text-[#736F6E]">
              Inscriptions enregistrées sur <span className="text-[#0D0D0D] font-medium">{window.location.host}</span>.
              Chaque adresse de site (prévisualisation / site en ligne) possède sa propre liste.
            </p>
          </div>
          <button data-testid="admin-export-csv-button" onClick={exportCsv}
            className="flex items-center gap-3 bg-[#0D0D0D] text-[#FAF8F5] text-[11px] uppercase tracking-[0.15em] px-8 py-4 rounded-full hover:bg-[#2a2a2a] transition-colors">
            <Download size={14} /> Exporter en CSV
          </button>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 border-t border-l border-black/[0.08] mb-14">
          {[
            { icon: Users, label: "Total inscrits", value: data?.total ?? "—", testid: "admin-stat-total" },
            { icon: CalendarCheck, label: "Aujourd'hui", value: todayCount, testid: "admin-stat-today" },
            { icon: Phone, label: "Avec téléphone", value: withPhone, testid: "admin-stat-phone" },
            { icon: MailCheck, label: "Consentement marketing", value: marketing, testid: "admin-stat-marketing" },
          ].map(({ icon: Icon, label, value, testid }) => (
            <div key={label} data-testid={testid} className="border-r border-b border-black/[0.08] p-6 sm:p-8">
              <Icon size={18} strokeWidth={1.5} className="text-[#A8B89A]" />
              <p className="font-display text-4xl sm:text-5xl mt-4 tracking-tight">{value}</p>
              <p className="font-mono-alt text-[10px] uppercase tracking-[0.2em] text-[#736F6E] mt-2">{label}</p>
            </div>
          ))}
        </div>

        <AdminFilters filters={filters} onChange={setFilters} resultCount={filtered.length} total={items.length} />

        <div className="overflow-x-auto border border-black/[0.08]">
          <table data-testid="admin-table" className="w-full text-sm">
            <thead>
              <tr className="border-b border-black/[0.08] bg-[#F3EFEA]">
                {["Date", "Prénom", "Nom", "Email", "Téléphone", "Pointure", "Préférence", "Marketing"].map((h) => (
                  <th key={h} className="text-left font-mono-alt text-[10px] uppercase tracking-[0.2em] text-[#736F6E] px-5 py-4 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="border-b border-black/[0.05] hover:bg-[#F3EFEA]/60 transition-colors">
                  <td className="px-5 py-4 whitespace-nowrap text-[#6E6A66]">{new Date(r.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}</td>
                  <td className="px-5 py-4">{r.first_name}</td>
                  <td className="px-5 py-4">{r.last_name}</td>
                  <td className="px-5 py-4">{r.email}</td>
                  <td className="px-5 py-4 text-[#6E6A66]">{r.phone || "—"}</td>
                  <td className="px-5 py-4">{r.shoe_size}</td>
                  <td className="px-5 py-4 capitalize">{r.preference || "—"}</td>
                  <td className="px-5 py-4">{r.marketing_consent ? "Oui" : "Non"}</td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td data-testid="admin-table-empty" colSpan={8} className="px-5 py-14 text-center text-[#6E6A66]">
                  {items.length === 0 ? "Aucune pré-réservation pour le moment." : "Aucune inscription ne correspond à votre recherche."}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
